/**
 * Send a couple their own password-reset link.
 *
 * THE COPY IS THE FEATURE. An operator opening a dialog labelled "reset
 * password" reasonably expects to be handed a password, and the honest answer
 * — that staff cannot see or set one, and that the couple gets a single-use
 * link instead — has to be on the screen or it will be asked in a support
 * ticket every time.
 *
 * Confirm-gated: nothing is sent until the button is pressed.
 */

import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Stack, Alert,
} from '@mui/material';

interface Props {
  open: boolean;
  email: string;
  onClose: () => void;
  onConfirm: () => void;
}

/** `grant@example.com` → `gr***@example.com`. Same shape the server logs. */
function maskEmail(email: string): string {
  return String(email || '').replace(/(.{2}).*(@.*)/, '$1***$2');
}

const PasswordResetDialog: React.FC<Props> = ({ open, email, onClose, onConfirm }) => (
  <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
    <DialogTitle>Send a password reset</DialogTitle>
    <DialogContent dividers>
      <Stack spacing={2}>
        <Typography variant="body2">
          This sends a reset link to <strong>{maskEmail(email)}</strong>.
        </Typography>
        <Alert severity="info" variant="outlined">
          <Typography variant="body2">
            LifeScribe staff cannot see or set a password. The link is
            single-use, expires on its own, and signing in with it ends every
            other session the couple has open.
          </Typography>
        </Alert>
        <Typography variant="caption" color="text.secondary">
          The wedding service allows three reset requests per hour for one
          address. A fourth is refused rather than queued.
        </Typography>
      </Stack>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Cancel</Button>
      <Button onClick={onConfirm} variant="contained">Send reset link</Button>
    </DialogActions>
  </Dialog>
);

export default PasswordResetDialog;
